import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import IncludePropertyInEvents from "./include-feature-flag-property-in-backend-events.mdx";
import APIOverrideServerProperties from './override-server-properties/api.mdx';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const MultiLanguage = makeShortcode("MultiLanguage");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`There are 3 steps to implement feature flags using the PostHog API:`}</p>
    <h3 {...{
      "id": "step-1-evaluate-the-feature-flag-value-using-decide"
    }}>{`Step 1: Evaluate the feature flag value using `}<inlineCode parentName="h3">{`decide`}</inlineCode></h3>
    <p><inlineCode parentName="p">{`decide`}</inlineCode>{` is the endpoint used to determine if a given flag is enabled for a certain user or not.`}</p>
    <h4 {...{
      "id": "request"
    }}>{`Request`}</h4>
    <MultiLanguage mdxType="MultiLanguage">
      <pre><code parentName="pre" {...{
          "className": "language-shell"
        }}>{`curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "distinct_id_of_your_user",
    "groups" : {
        "group_type": "group_id"
    }
}' <ph_client_api_host>/decide?v=3 
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import requests
import json

url = "<ph_client_api_host>/decide?v=3"
headers = {
    "Content-Type": "application/json"
}
payload = {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "user distinct id",
    "groups": {
        "group_type": "group_id"
    }
}
response = requests.post(url, headers=headers, data=json.dumps(payload))
print(response.json())
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`import fetch from "node-fetch";

async function sendDecideRequest() {
    const url = "<ph_client_api_host>/decide?v=3";
    const headers = {
        "Content-Type": "application/json",
    };
    const payload = {
        api_key: "<ph_project_api_key>",
        distinct_id: "user distinct id",
        groups: {
            group_type: "group_id",
        },
    };

    const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(payload),
    });
    const data = await response.json();
    console.log(data);
}

sendDecideRequest();
`}</code></pre>
    </MultiLanguage>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` The `}<inlineCode parentName="p">{`groups`}</inlineCode>{` key is only required for group-based feature flags. If you use it, replace `}<inlineCode parentName="p">{`group_type`}</inlineCode>{` and `}<inlineCode parentName="p">{`group_id`}</inlineCode>{` with the values for your group such as `}<inlineCode parentName="p">{`company: "Twitter"`}</inlineCode>{`.`}</p>
    </blockquote>
    <h4 {...{
      "id": "response"
    }}>{`Response`}</h4>
    <p>{`When called, `}<inlineCode parentName="p">{`decide`}</inlineCode>{` returns the flag values and payloads, along with more information.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "config": {
    "enable_collect_everything": true
  },
  "toolbarParams": {},
  "errorComputingFlags": false,
  "isAuthenticated": false,
  "supportedCompression": [
    "gzip",
    "lz64"
  ],
  "featureFlags": {
    "my-awesome-flag": true,
    "my-awesome-flag-2": true,
    "my-multivariate-flag": "some-string-value",
    "flag-thats-not-on": false,
  },
  "featureFlagPayloads": {
    "my-awesome-flag": "example-payload-string",
    "my-awesome-flag-2": "{\\"color\\": \\"blue\\", \\"animal\\": \\"hedgehog\\"}"
  }
}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` `}<inlineCode parentName="p">{`errorComputingFlags`}</inlineCode>{` will return `}<inlineCode parentName="p">{`true`}</inlineCode>{` if we didn't manage to compute some flags (for example, if there's an `}<a parentName="p" {...{
          "href": "https://status.posthog.com/"
        }}>{`ongoing incident involving flag evaluation`}</a>{`). `}</p>
      <p parentName="blockquote">{`This enables partial updates to currently active flags in your clients.`}</p>
    </blockquote>

    <IncludePropertyInEvents mdxType="IncludePropertyInEvents" />
    <p>{`To do this, include the `}<inlineCode parentName="p">{`$feature/feature_flag_name`}</inlineCode>{` property in your event:`}</p>
    <MultiLanguage mdxType="MultiLanguage">
      <pre><code parentName="pre" {...{
          "className": "language-shell"
        }}>{`curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "event": "your_event_name",
    "distinct_id": "distinct_id_of_your_user",
    "properties": {
      "$feature/feature-flag-key": "variant-key" # Replace feature-flag-key with your flag key. Replace 'variant-key' with the key of your variant
    }
}' <ph_client_api_host>/capture/ 
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import requests
import json

url = "<ph_client_api_host>/capture/"
headers = {
    "Content-Type": "application/json"
}
payload = {
    "api_key": "<ph_project_api_key>", 
    "event": "your_event_name",
    "distinct_id": "distinct_id_of_your_user,
    "properties": {
      "$feature/feature-flag-key": "variant-key" # Replace feature-flag-key with your flag key. Replace 'variant-key' with the key of your variant
    }
}
response = requests.post(url, headers=headers, data=json.dumps(payload))
print(response)
`}</code></pre>
    </MultiLanguage>
    <h3 {...{
      "id": "step-3-send-a-feature_flag_called-event"
    }}>{`Step 3: Send a `}<inlineCode parentName="h3">{`$feature_flag_called`}</inlineCode>{` event`}</h3>
    <p>{`To track usage of your feature flag and view related analytics in PostHog, submit the `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` event whenever you check a feature flag value in your code.`}</p>
    <p>{`You need to include two properties with this event:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`$feature_flag_response`}</inlineCode>{`: This is the name of the variant the user has been assigned to e.g., "control" or "test"`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`$feature_flag`}</inlineCode>{`: This is the key of the feature flag in your experiment.`}</li>
    </ol>
    <MultiLanguage mdxType="MultiLanguage">
      <pre><code parentName="pre" {...{
          "className": "language-shell"
        }}>{`curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "event": "$feature_flag_called",
    "distinct_id": "distinct_id_of_your_user",
    "properties": {
      "$feature_flag": "feature-flag-key",
      "$feature_flag_response": "variant-name"
    }
}' <ph_client_api_host>/capture/ 
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import requests
import json

url = "<ph_client_api_host>/capture/"
headers = {
    "Content-Type": "application/json"
}
payload = {
    "api_key": "<ph_project_api_key>", 
    "event": "feature_flag_called",
    "distinct_id": "distinct_id_of_your_user,
    "properties": {
      "$feature_flag": "feature-flag-key",
      "$feature_flag_response": "variant-name"
    }
}
response = requests.post(url, headers=headers, data=json.dumps(payload))
print(response)
`}</code></pre>
    </MultiLanguage>

    <APIOverrideServerProperties mdxType="APIOverrideServerProperties" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      